import { ModifiedPermissionCollection } from 'app/auth/state/reducers/permissionExtension'
import useGetReturnSettings from 'app/settings/components/ReturnsSettings/use-get-return-settings'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { AddPermissionModificationRMA, RemovePermissionModificationRMA } from './actions'

export interface IPermissionExtensionContextValue {
  hasBeenProvided: boolean
  modifiedPermissions: ModifiedPermissionCollection
}

export const PermissionExtensionContext = React.createContext<IPermissionExtensionContextValue | undefined>(undefined)

interface Props {}

/**
 * useRMAPermissionModification will trigger the fetching of the RMA setting, and store whether the RMA permissions should be modified
 */
const useRMAPermissionModification = (isPortal: boolean) => {
  const dispatch = useDispatch()
  const { data, isLoading } = useGetReturnSettings()

  // set the redux state for this whenever the the data is changed
  useEffect(() => {
    if (isLoading) {
      return
    }
    if (!isPortal || data?.Data?.OfflineRMA?.IsEnabled) {
      // RMA enabled, remove any permission modifications related to RMA
      dispatch(RemovePermissionModificationRMA())
    } else {
      // RMA disabled, add permission modifications to hide menu's and dialogs
      dispatch(AddPermissionModificationRMA())
    }
  }, [data?.Data?.OfflineRMA?.IsEnabled, isLoading])

  return useMemo(() => ({ isLoading }), [isLoading])
}

/**
 *  PermissionExtensionContextProvider will keep track of modifications on the permissions based on user settings
 */
export const PermissionExtensionContextProvider: React.FC<Props> = ({ children }) => {
  const isPortal = useIsPortal()
  const { isLoading } = useRMAPermissionModification(isPortal)

  const modifiedPermissions = useSelector<IRootReducerState, ModifiedPermissionCollection>(
    state => state.auth.permissionExtension.modifiedPermissions
  )

  const contextValue = useMemo<IPermissionExtensionContextValue>(
    () => ({ hasBeenProvided: true, modifiedPermissions }),
    [modifiedPermissions]
  )
  return (
    <PermissionExtensionContext.Provider value={contextValue}>
      {/* do not render children when things are still loading, it might 404 on permissionRoutes for the permission is still loading */}
      {!isLoading && children}
    </PermissionExtensionContext.Provider>
  )
}

export function usePermissionExtensionContext() {
  const context = React.useContext(PermissionExtensionContext)
  if (context === undefined || !context.hasBeenProvided) {
    console.error('usePermissionExtensionContext must be used within an PermissionExtensionContext')
  }

  return context as IPermissionExtensionContextValue
}
